import { makeAutoObservable } from "mobx";

import { regularTheme, themes } from "@/components/expression/theme/corca-themes";
import {
  CODE_FONT_SIZES,
  DEFAULT_CODE_FONT_SIZE,
} from "@/components/tiptap/code-block/codeFontSize";

import type { NodeTheme } from "@/components/expression/theme/types";
import type { CodeFontSize } from "@/components/tiptap/code-block/codeFontSize";
import type { RootStore } from "@/store";

export const MATH_THEME_STORAGE_KEY = "math-theme";
export const CODE_FONT_SIZE_STORAGE_KEY = "code-font-size";
export const CODE_THEME_STORAGE_KEY = "code-theme";

export const DEFAULT_THEME = regularTheme;

export type CodeTheme = "light" | "dark" | null;

/**
 * Reads a raw localStorage value and assigns it to the matching field, falling back to the
 * default when the value is absent/invalid. Shared by initial load and the cross-window
 * storage listener.
 */
type Setting = {
  key: string;
  apply(store: UserSettingsStore, raw: string | null): void;
};

const SETTINGS: Setting[] = [
  {
    key: MATH_THEME_STORAGE_KEY,
    apply(store, raw) {
      const theme = raw ? themes.find(({ id }) => id === raw) : undefined;
      store.mathTheme = theme ?? DEFAULT_THEME;
    },
  },
  {
    key: CODE_FONT_SIZE_STORAGE_KEY,
    apply(store, raw) {
      const size = Number(raw) as CodeFontSize;
      store.codeFontSize = CODE_FONT_SIZES.includes(size) ? size : DEFAULT_CODE_FONT_SIZE;
    },
  },
  {
    key: CODE_THEME_STORAGE_KEY,
    apply(store, raw) {
      store.codeTheme = raw === "light" || raw === "dark" ? raw : null;
    },
  },
];

export class UserSettingsStore {
  rootStore: RootStore;

  mathTheme: NodeTheme = DEFAULT_THEME;

  codeFontSize: CodeFontSize = DEFAULT_CODE_FONT_SIZE;

  codeTheme: CodeTheme = null;

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;

    makeAutoObservable(this, undefined, { autoBind: true });
    this.load();

    // Sync settings changed in other browser windows into this one.
    if (typeof window !== "undefined") {
      window.addEventListener("storage", this.handleStorageEvent);
    }
  }

  setMathTheme(theme: NodeTheme) {
    this.mathTheme = theme;
    localStorage.setItem(MATH_THEME_STORAGE_KEY, theme.id);
  }

  setCodeFontSize(size: CodeFontSize) {
    this.codeFontSize = size;
    localStorage.setItem(CODE_FONT_SIZE_STORAGE_KEY, String(size));
  }

  setCodeTheme(theme: CodeTheme) {
    this.codeTheme = theme;

    if (theme) {
      localStorage.setItem(CODE_THEME_STORAGE_KEY, theme);
    } else {
      localStorage.removeItem(CODE_THEME_STORAGE_KEY);
    }
  }

  private load() {
    if (typeof window === "undefined") {
      return;
    }

    for (const setting of SETTINGS) {
      setting.apply(this, localStorage.getItem(setting.key));
    }

    // Preserve prior behavior: math theme and font size write their default back on empty load.
    if (!localStorage.getItem(MATH_THEME_STORAGE_KEY)) {
      localStorage.setItem(MATH_THEME_STORAGE_KEY, this.mathTheme.id);
    }
    if (!localStorage.getItem(CODE_FONT_SIZE_STORAGE_KEY)) {
      localStorage.setItem(CODE_FONT_SIZE_STORAGE_KEY, String(this.codeFontSize));
    }
  }

  dispose() {
    if (typeof window !== "undefined") {
      window.removeEventListener("storage", this.handleStorageEvent);
    }
  }

  private handleStorageEvent(event: StorageEvent) {
    const setting = SETTINGS.find(({ key }) => key === event.key);
    setting?.apply(this, event.newValue);
  }
}
